import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { changePassword } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../../types/portal';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--page-bg, linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-blue) 100%));
  padding: 1rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  padding: 1.25rem;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem 0;
  font-size: 1.4rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 2px solid rgba(102,126,234,0.2);
  margin-bottom: 0.75rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #11998e, #38ef7d);
  cursor: pointer;
`;

const Msg = styled.div<{ type: 'error'|'success' }>`
  margin-bottom: 0.75rem;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  color: #fff;
  background: ${p => p.type === 'success' ? 'linear-gradient(135deg,#11998e,#38ef7d)' : 'linear-gradient(135deg,#fc4a1a,#f7b733)'};
`;

const Hint = styled.p`
  margin-top: 0;
  color: #666;
`;

const SetPasswordRequired: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);

  const [current, setCurrent] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [msg, setMsg] = useState<string|null>(null);
  const [err, setErr] = useState<string|null>(null);
  const [saving, setSaving] = useState(false);

  const requireChange = !!user?.requirePasswordChange;

  useEffect(() => {
    // If flag cleared or user missing, go to dashboard
    if (!user) return;
    if (!requireChange) {
      const dest = user.role === UserRole.TEACHER ? '/portal/teacher' : user.role === UserRole.ADMIN ? '/portal/admin' : '/portal/student';
      navigate(dest, { replace: true });
    }
  }, [user, requireChange, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setMsg(null);
    // Client-side policy
    if (p1.length < 9) { setErr('Password must be at least 9 characters'); return; }
    const hasUpper = /[A-Z]/.test(p1);
    const hasLower = /[a-z]/.test(p1);
    const hasDigit = /[0-9]/.test(p1);
    const hasSpecial = /[^A-Za-z0-9]/.test(p1);
    if (!(hasUpper && hasLower && hasDigit && hasSpecial)) {
      setErr('Password must include uppercase, lowercase, number, and special character');
      return;
    }
    if (p1 !== p2) { setErr('Passwords do not match'); return; }
    try {
      setSaving(true);
      const payload: any = { newPassword: p1 };
      if (!requireChange) payload.currentPassword = current;
      await dispatch(changePassword(payload)).unwrap();
      setMsg('Password updated successfully');
      setTimeout(() => {
        if (user) {
          const dest = user.role === UserRole.TEACHER ? '/portal/teacher' : user.role === UserRole.ADMIN ? '/portal/admin' : '/portal/student';
          navigate(dest, { replace: true });
        } else {
          navigate('/portal/login', { replace: true });
        }
      }, 800);
    } catch (e: any) {
      setErr(e?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>Set a new password</Title>
        <Hint>{requireChange ? 'For security, please create a new password to continue.' : 'Change your password.'}</Hint>
        {msg && <Msg type="success">✅ {msg}</Msg>}
        {err && <Msg type="error">⚠️ {err}</Msg>}
        <form onSubmit={onSubmit}>
          {!requireChange && (
            <Input type="password" placeholder="Current password" value={current} onChange={e => setCurrent(e.target.value)} />
          )}
          <Input type="password" placeholder="New password" value={p1} onChange={e => setP1(e.target.value)} />
          <Input type="password" placeholder="Confirm new password" value={p2} onChange={e => setP2(e.target.value)} />
          <Button disabled={saving}>{saving ? 'Saving…' : 'Save password'}</Button>
        </form>
      </Card>
    </Wrapper>
  );
};

export default SetPasswordRequired;
